/**
 * React Query hooks + view helpers for documents. Optimistic updates
 * keep the UI snappy; on rollback the previous cache snapshot is
 * restored. The optimistic data is the source of truth offline — these
 * caches are persisted to IndexedDB by the query client setup.
 *
 * The page's organising lens is expiry ("what's expiring"), so the
 * grouping/formatting helpers here are all about the expiry horizon.
 */

import type {
  DocumentCreateInput,
  DocumentDTO,
  DocumentUpdateInput,
} from '@almanac/shared';
import { deriveDocumentFileName, vaultNextReviewIso } from '@almanac/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';
import { mutationKeys, queryKeys } from './queryClient.js';

export function useDocuments() {
  return useQuery<DocumentDTO[]>({
    queryKey: queryKeys.documents,
    queryFn: api.listDocuments,
  });
}

type DocumentListUpdater = (previous: DocumentDTO[] | undefined) => DocumentDTO[] | undefined;

function patchDocuments(qc: ReturnType<typeof useQueryClient>, updater: DocumentListUpdater) {
  qc.setQueryData<DocumentDTO[]>(queryKeys.documents, (prev) => updater(prev));
}

export function useCreateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: mutationKeys.createDocument,
    mutationFn: (input: DocumentCreateInput & { id: string }) => api.createDocument(input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: queryKeys.documents });
      const previous = qc.getQueryData<DocumentDTO[]>(queryKeys.documents);
      const now = new Date().toISOString();
      const optimistic: DocumentDTO = {
        id: input.id,
        title: input.title,
        lifeAreaId: input.lifeAreaId,
        documentCategory: input.documentCategory ?? 'routine',
        category: input.category ?? null,
        subArea: input.subArea ?? null,
        pertainsTo: input.pertainsTo ?? null,
        starred: input.starred ?? false,
        fileName: null,
        mimeType: null,
        fileSize: null,
        fileHash: null,
        tripId: input.tripId ?? null,
        issuer: input.issuer ?? null,
        issuedDate: input.issuedDate ?? null,
        expiryDate: input.expiryDate ?? null,
        expiryDismissedFor: input.expiryDismissedFor ?? null,
        vaultReviewedAt: null,
        vaultReviewMonths: input.vaultReviewMonths ?? null,
        dropboxPath: input.dropboxPath ?? null,
        dropboxVaultPath: null,
        tags: input.tags ?? null,
        notes: input.notes ?? null,
        visibility: input.visibility ?? 'private',
        createdBy: 'me',
        createdAt: now,
        updatedAt: now,
      };
      patchDocuments(qc, (prev) => [...(prev ?? []), optimistic]);
      return { previous };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.documents, ctx.previous);
    },
    onSuccess: (server) => {
      patchDocuments(qc, (prev) => (prev ?? []).map((d) => (d.id === server.id ? server : d)));
    },
  });
}

export function useUpdateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: mutationKeys.updateDocument,
    mutationFn: (vars: { id: string; input: DocumentUpdateInput }) =>
      api.updateDocument(vars.id, vars.input),
    onMutate: async ({ id, input }) => {
      await qc.cancelQueries({ queryKey: queryKeys.documents });
      const previous = qc.getQueryData<DocumentDTO[]>(queryKeys.documents);
      patchDocuments(qc, (prev) =>
        (prev ?? []).map((d) =>
          d.id === id
            ? {
                ...d,
                ...input,
                category: input.category === undefined ? d.category : (input.category ?? null),
                subArea: input.subArea === undefined ? d.subArea : (input.subArea ?? null),
                pertainsTo:
                  input.pertainsTo === undefined ? d.pertainsTo : (input.pertainsTo ?? null),
                issuer: input.issuer === undefined ? d.issuer : (input.issuer ?? null),
                issuedDate:
                  input.issuedDate === undefined ? d.issuedDate : (input.issuedDate ?? null),
                expiryDate:
                  input.expiryDate === undefined ? d.expiryDate : (input.expiryDate ?? null),
                expiryDismissedFor:
                  input.expiryDismissedFor === undefined
                    ? d.expiryDismissedFor
                    : (input.expiryDismissedFor ?? null),
                dropboxPath:
                  input.dropboxPath === undefined ? d.dropboxPath : (input.dropboxPath ?? null),
                tags: input.tags === undefined ? d.tags : (input.tags ?? null),
                notes: input.notes === undefined ? d.notes : (input.notes ?? null),
                updatedAt: new Date().toISOString(),
              }
            : d,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.documents, ctx.previous);
    },
    onSuccess: (server) => {
      patchDocuments(qc, (prev) => (prev ?? []).map((d) => (d.id === server.id ? server : d)));
    },
  });
}

/**
 * Upload (or replace) a document's file. Online-only on purpose — a File
 * can't be serialised into the offline replay queue — so this mutation
 * carries no persisted mutationKey. Optimistically reflects the filename
 * so the row shows its paperclip immediately; the server response fills
 * in the rest (size, mime).
 */
export function useUploadDocumentFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; file: File }) =>
      api.uploadDocumentFile(vars.id, vars.file),
    onMutate: async ({ id, file }) => {
      await qc.cancelQueries({ queryKey: queryKeys.documents });
      const previous = qc.getQueryData<DocumentDTO[]>(queryKeys.documents);
      patchDocuments(qc, (prev) =>
        (prev ?? []).map((d) =>
          d.id === id
            ? { ...d, fileName: file.name, mimeType: file.type || d.mimeType, fileSize: file.size }
            : d,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.documents, ctx.previous);
    },
    onSuccess: (server) => {
      patchDocuments(qc, (prev) => (prev ?? []).map((d) => (d.id === server.id ? server : d)));
    },
  });
}

/**
 * Detach and permanently delete a document's file, keeping the record.
 * Online-only (it hits the file endpoint, like upload); optimistically
 * clears the file metadata so the paperclip/Open affordance disappears
 * immediately.
 */
export function useRemoveDocumentFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string }) => api.removeDocumentFile(vars.id),
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: queryKeys.documents });
      const previous = qc.getQueryData<DocumentDTO[]>(queryKeys.documents);
      patchDocuments(qc, (prev) =>
        (prev ?? []).map((d) =>
          d.id === id ? { ...d, fileName: null, mimeType: null, fileSize: null } : d,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.documents, ctx.previous);
    },
    onSuccess: (server) => {
      patchDocuments(qc, (prev) => (prev ?? []).map((d) => (d.id === server.id ? server : d)));
    },
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: mutationKeys.deleteDocument,
    mutationFn: (vars: { id: string }) => api.deleteDocument(vars.id),
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: queryKeys.documents });
      const previous = qc.getQueryData<DocumentDTO[]>(queryKeys.documents);
      patchDocuments(qc, (prev) => (prev ?? []).filter((d) => d.id !== id));
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.documents, ctx.previous);
    },
  });
}

/**
 * Restore a just-deleted document — the "Undo" on the delete toast. The
 * whole document is passed in so `onMutate` can re-insert it into the
 * list optimistically (the delete removed it), then the server response
 * reconciles. Runs from an unmounted caller (the drawer closes on
 * delete), so all cache work lives in the hook callbacks, not per-call.
 */
export function useRestoreDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (doc: DocumentDTO) => api.restoreDocument(doc.id),
    onMutate: async (doc) => {
      await qc.cancelQueries({ queryKey: queryKeys.documents });
      const previous = qc.getQueryData<DocumentDTO[]>(queryKeys.documents);
      patchDocuments(qc, (prev) => {
        const list = prev ?? [];
        return list.some((d) => d.id === doc.id) ? list : [...list, doc];
      });
      return { previous };
    },
    onError: (_err, _doc, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.documents, ctx.previous);
    },
    onSuccess: (server) => {
      patchDocuments(qc, (prev) => (prev ?? []).map((d) => (d.id === server.id ? server : d)));
    },
  });
}

// ── Expiry horizon helpers ──────────────────────────────────────────

/** Documents whose expiry falls within this many days read as "soon". */
export const EXPIRY_SOON_DAYS = 60;

export type ExpiryStatus = 'expired' | 'soon' | 'later' | 'none';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** Whole-day delta from `now` to the expiry date (negative = past). */
function daysUntilExpiry(expiryDate: string, now: Date): number {
  const expiry = new Date(`${expiryDate.slice(0, 10)}T00:00:00`);
  const today = new Date(`${now.toISOString().slice(0, 10)}T00:00:00`);
  return Math.round((expiry.getTime() - today.getTime()) / MS_PER_DAY);
}

export function expiryStatus(doc: DocumentDTO, now: Date): ExpiryStatus {
  if (!doc.expiryDate) return 'none';
  const days = daysUntilExpiry(doc.expiryDate, now);
  if (days < 0) return 'expired';
  if (days <= EXPIRY_SOON_DAYS) return 'soon';
  return 'later';
}

/** Human label for the right-hand column, e.g. "Expires in 12 days". */
export function formatExpiry(doc: DocumentDTO, now: Date): string | null {
  if (!doc.expiryDate) return null;
  const days = daysUntilExpiry(doc.expiryDate, now);
  if (days < -1) return `Expired ${-days} days ago`;
  if (days === -1) return 'Expired yesterday';
  if (days === 0) return 'Expires today';
  if (days === 1) return 'Expires tomorrow';
  if (days <= EXPIRY_SOON_DAYS) return `Expires in ${days} days`;
  const d = new Date(`${doc.expiryDate.slice(0, 10)}T00:00:00`);
  return `Expires ${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

interface DocumentGroup {
  // 'expired'/'soon'/… for the expiry lens; 'person:<id>' / 'kind:<x>'
  // for the other lenses. Used only as a React list key + heading.
  key: string;
  label: string;
  documents: DocumentDTO[];
}

const GROUP_ORDER: { key: ExpiryStatus; label: string }[] = [
  { key: 'expired', label: 'Expired' },
  { key: 'soon', label: 'Expiring soon' },
  { key: 'later', label: 'Later' },
  { key: 'none', label: 'No expiry date' },
];

/**
 * Group documents by expiry horizon for the page. Within each dated
 * group items sort by soonest expiry first; the undated group sorts by
 * title so it reads like a filing cabinet.
 */
export function groupDocuments(docs: DocumentDTO[], now: Date): DocumentGroup[] {
  const buckets = new Map<ExpiryStatus, DocumentDTO[]>();
  for (const doc of docs) {
    const key = expiryStatus(doc, now);
    const list = buckets.get(key);
    if (list) list.push(doc);
    else buckets.set(key, [doc]);
  }
  const groups: DocumentGroup[] = [];
  for (const { key, label } of GROUP_ORDER) {
    const items = buckets.get(key);
    if (!items || items.length === 0) continue;
    items.sort((a, b) => {
      if (key === 'none') return a.title.localeCompare(b.title);
      return (a.expiryDate ?? '').localeCompare(b.expiryDate ?? '');
    });
    groups.push({ key, label, documents: items });
  }
  return groups;
}

// ── Lenses: the index can shelve documents three ways ───────────────
// by person (who it's about), by kind (category), or by life area. The
// expiry horizon is surfaced separately, as a "needs attention" strip.

export type DocumentLens = 'person' | 'kind' | 'area';

/** Sort within a non-expiry group: soonest expiry first, then title, so
 *  dated items lead and the rest reads like a filing cabinet. */
function byExpiryThenTitle(a: DocumentDTO, b: DocumentDTO): number {
  const ae = a.expiryDate ?? '';
  const be = b.expiryDate ?? '';
  if (ae && be && ae !== be) return ae.localeCompare(be);
  if (ae && !be) return -1;
  if (!ae && be) return 1;
  return a.title.localeCompare(b.title);
}

/**
 * Group by who the document is about. A document about several people
 * appears under each of them (the family policy is genuinely one of
 * Marie's documents AND one of Hugo's). Documents about nobody in
 * particular fall into a trailing "Not about anyone in particular"
 * group. Person groups sort by count (most-documented first), then name.
 */
export function groupDocumentsByPerson(
  docs: DocumentDTO[],
  peopleById: Map<string, string>,
): DocumentGroup[] {
  const byPerson = new Map<string, DocumentDTO[]>();
  const unassigned: DocumentDTO[] = [];
  for (const doc of docs) {
    const ids = doc.pertainsTo ?? [];
    if (ids.length === 0) {
      unassigned.push(doc);
      continue;
    }
    for (const id of ids) {
      const list = byPerson.get(id);
      if (list) list.push(doc);
      else byPerson.set(id, [doc]);
    }
  }
  const groups: DocumentGroup[] = [...byPerson.entries()]
    .map(([id, items]) => ({
      key: `person:${id}`,
      label: peopleById.get(id) ?? 'Unknown person',
      documents: items.sort(byExpiryThenTitle),
    }))
    .sort((a, b) =>
      b.documents.length - a.documents.length || a.label.localeCompare(b.label),
    );
  if (unassigned.length > 0) {
    groups.push({
      key: 'person:none',
      label: 'Not about anyone in particular',
      documents: unassigned.sort(byExpiryThenTitle),
    });
  }
  return groups;
}

/**
 * Group by kind — the finer `category` (Medical, Insurance, Receipt…).
 * Uncategorised documents trail in their own group. Groups sort by
 * count then label.
 */
export function groupDocumentsByKind(docs: DocumentDTO[]): DocumentGroup[] {
  const byKind = new Map<string, DocumentDTO[]>();
  const uncategorised: DocumentDTO[] = [];
  for (const doc of docs) {
    const kind = doc.category?.trim();
    if (!kind) {
      uncategorised.push(doc);
      continue;
    }
    // Case-insensitive bucket keyed by the first-seen spelling.
    const existingKey = [...byKind.keys()].find((k) => k.toLowerCase() === kind.toLowerCase());
    const key = existingKey ?? kind;
    const list = byKind.get(key);
    if (list) list.push(doc);
    else byKind.set(key, [doc]);
  }
  const groups: DocumentGroup[] = [...byKind.entries()]
    .map(([label, items]) => ({
      key: `kind:${label.toLowerCase()}`,
      label,
      documents: items.sort(byExpiryThenTitle),
    }))
    .sort((a, b) =>
      b.documents.length - a.documents.length || a.label.localeCompare(b.label),
    );
  if (uncategorised.length > 0) {
    groups.push({
      key: 'kind:none',
      label: 'Uncategorised',
      documents: uncategorised.sort(byExpiryThenTitle),
    });
  }
  return groups;
}

/** True when the user has dismissed expiry attention for the document's
 *  current expiry date ("I've handled it" — quietly, or via a renewal
 *  obligation). The stored dismissal names the expiry date it applies
 *  to, so editing the expiry (e.g. after a renewal) makes it stale and
 *  the attention surfaces resume on their own. */
export function isExpiryHandled(doc: DocumentDTO): boolean {
  return Boolean(
    doc.expiryDate && doc.expiryDismissedFor === doc.expiryDate.slice(0, 10),
  );
}

/** Count of documents currently expired or expiring soon — for the
 *  Today tile's summary line and the index attention banner. Handled
 *  documents don't count: the user has seen them and either dismissed
 *  the reminder or moved it into a renewal obligation. */
export function expiringCount(docs: DocumentDTO[], now: Date): number {
  return docs.filter((d) => {
    if (isExpiryHandled(d)) return false;
    const s = expiryStatus(d, now);
    return s === 'expired' || s === 'soon';
  }).length;
}

/**
 * Status line for the Documents tile on the Today screen. Leads with
 * the pressing signal (expiring/expired) when there is one, otherwise
 * falls back to a plain count — mirroring the obligation tile's "most
 * pressing first" behaviour. Returns undefined while the cache is
 * empty so the tile shows its generic "View all".
 */
export function documentsTileSummary(docs: DocumentDTO[] | undefined, now: Date): string | undefined {
  if (!docs || docs.length === 0) return undefined;
  const expiring = expiringCount(docs, now);
  if (expiring > 0) return `${expiring} expiring soon`;
  const noun = docs.length === 1 ? 'record' : 'records';
  return `${docs.length} ${noun}`;
}

/**
 * Group by life area. Documents with no/unknown area trail in their own
 * group. Groups sort by count then label — same shape as the other two
 * lenses so the index reads identically whichever axis is chosen.
 */
export function groupDocumentsByArea(
  docs: DocumentDTO[],
  areaById: Map<string, string>,
): DocumentGroup[] {
  const byArea = new Map<string, DocumentDTO[]>();
  for (const doc of docs) {
    const id = doc.lifeAreaId || '__none__';
    const list = byArea.get(id);
    if (list) list.push(doc);
    else byArea.set(id, [doc]);
  }
  return [...byArea.entries()]
    .map(([id, items]) => ({
      key: `area:${id}`,
      label: areaById.get(id) ?? 'No life area',
      documents: items.sort(byExpiryThenTitle),
    }))
    .sort((a, b) => b.documents.length - a.documents.length || a.label.localeCompare(b.label));
}

// ── Folder index + drill-down (Phase 1 scalable browse) ─────────────
// The page is an index of facet "folders" with counts; tapping one
// drills into just that subset, newest-first and sub-grouped by time.

/** Sentinel id for the "nobody in particular" / "no area" folders. */
export const NO_FACET = '__none__';

export interface DocumentFolder {
  /** Stable React key. */
  key: string;
  /** Folder display name (person/kind/area). */
  label: string;
  /** Two-letter initials for the person-lens avatar; absent otherwise. */
  initials?: string;
  count: number;
  /** The filter that drilling into this folder applies. */
  filter: DocumentFilter;
}

/**
 * A stacking filter. Several combine with AND to carve a precise slice
 * (person + kind + year + free text), mirroring the search screen.
 */
export type DocumentFilter =
  | { type: 'person'; id: string; label: string }
  | { type: 'kind'; value: string; label: string }
  | { type: 'subarea'; value: string; label: string }
  | { type: 'tag'; value: string; label: string }
  | { type: 'area'; id: string; label: string }
  | { type: 'year'; value: number; label: string }
  | { type: 'expiry'; label: string };

/** The "filed" date a document sorts and chunks by: its issued date when
 *  known, else when it was added. Drives newest-first ordering and the
 *  year/month groupings. */
export function documentDate(doc: DocumentDTO): Date {
  const raw = (doc.issuedDate ?? doc.createdAt).slice(0, 10);
  const d = new Date(`${raw}T00:00:00`);
  return Number.isNaN(d.getTime()) ? new Date(doc.createdAt) : d;
}

function shortDate(d: Date, now: Date): string {
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  }
  return `${d.toLocaleDateString(undefined, { month: 'short' })} ’${String(d.getFullYear()).slice(2)}`;
}

/** The right-hand date column for a drill-down row. Expiring/expired
 *  documents show their expiry date in the accent (warn) colour;
 *  everything else shows its filed date, calmly. */
export function formatRowDate(doc: DocumentDTO, now: Date): { label: string; warn: boolean } {
  const status = expiryStatus(doc, now);
  if ((status === 'expired' || status === 'soon') && doc.expiryDate) {
    return { label: shortDate(new Date(`${doc.expiryDate.slice(0, 10)}T00:00:00`), now), warn: true };
  }
  return { label: shortDate(documentDate(doc), now), warn: false };
}

export function documentMatchesFilter(doc: DocumentDTO, f: DocumentFilter, now: Date): boolean {
  switch (f.type) {
    case 'person':
      return f.id === NO_FACET
        ? (doc.pertainsTo ?? []).length === 0
        : (doc.pertainsTo ?? []).includes(f.id);
    case 'kind':
      return f.value === NO_FACET
        ? !doc.category?.trim()
        : (doc.category ?? '').trim().toLowerCase() === f.value.toLowerCase();
    case 'subarea':
      return (doc.subArea ?? '').trim().toLowerCase() === f.value.toLowerCase();
    case 'tag':
      return (doc.tags ?? []).some((t) => t.trim().toLowerCase() === f.value.toLowerCase());
    case 'area':
      return f.id === NO_FACET ? !doc.lifeAreaId : doc.lifeAreaId === f.id;
    case 'year':
      return documentDate(doc).getFullYear() === f.value;
    case 'expiry': {
      const s = expiryStatus(doc, now);
      return s === 'expired' || s === 'soon';
    }
  }
}

/** Documents matching every active filter (AND). */
export function applyFilters(docs: DocumentDTO[], filters: DocumentFilter[], now: Date): DocumentDTO[] {
  if (filters.length === 0) return docs;
  return docs.filter((d) => filters.every((f) => documentMatchesFilter(d, f, now)));
}

export interface DatedGroup {
  key: string;
  label: string;
  documents: DocumentDTO[];
}

/** Newest-first, chunked This month / Earlier in <thisYear> / <year>…
 *  — the drill-down grouping for a person or area folder. */
export function groupByRecency(docs: DocumentDTO[], now: Date): DatedGroup[] {
  const sorted = [...docs].sort((a, b) => documentDate(b).getTime() - documentDate(a).getTime());
  const thisYear = now.getFullYear();
  const thisMonth = now.getMonth();
  const buckets = new Map<string, { label: string; order: number; docs: DocumentDTO[] }>();
  for (const doc of sorted) {
    const d = documentDate(doc);
    const y = d.getFullYear();
    let key: string;
    let label: string;
    let order: number;
    if (y === thisYear && d.getMonth() === thisMonth) {
      key = 'this-month';
      label = 'This month';
      order = 0;
    } else if (y === thisYear) {
      key = 'earlier-this-year';
      label = `Earlier in ${thisYear}`;
      order = 1;
    } else {
      key = `y${y}`;
      label = String(y);
      // Older years sort after the two current-year buckets, newest first.
      order = 2 + (thisYear - y);
    }
    const bucket = buckets.get(key);
    if (bucket) bucket.docs.push(doc);
    else buckets.set(key, { label, order, docs: [doc] });
  }
  return [...buckets.entries()]
    .sort((a, b) => a[1].order - b[1].order)
    .map(([key, b]) => ({ key, label: b.label, documents: b.docs }));
}

/** Newest-year-first, one group per calendar year — the drill-down
 *  grouping for a kind folder (year is the second axis). */
export function groupByYear(docs: DocumentDTO[]): DatedGroup[] {
  const byYear = new Map<number, DocumentDTO[]>();
  for (const doc of docs) {
    const y = documentDate(doc).getFullYear();
    const list = byYear.get(y);
    if (list) list.push(doc);
    else byYear.set(y, [doc]);
  }
  return [...byYear.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([year, items]) => ({
      key: `y${year}`,
      label: String(year),
      documents: items.sort((a, b) => documentDate(b).getTime() - documentDate(a).getTime()),
    }));
}

/** A facet value with how many of the set's documents carry it — the
 *  drill-down facet picker sheets list values alongside their counts. */
export type FacetCount<T extends string | number = string> = { value: T; count: number };

function countedValues(raw: (string | null | undefined)[]): FacetCount[] {
  const counts = new Map<string, number>();
  for (const r of raw) {
    const v = r?.trim();
    if (!v) continue;
    const existing = [...counts.keys()].find((c) => c.toLowerCase() === v.toLowerCase()) ?? v;
    counts.set(existing, (counts.get(existing) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([value, count]) => ({ value, count }));
}

/** Finer-categories present in a set, deduped case-insensitively, by
 *  descending frequency, with counts — the "Kind" facet offered inside a
 *  person/area drill-down. */
export function kindCounts(docs: DocumentDTO[]): FacetCount[] {
  return countedValues(docs.map((d) => d.category));
}

export function distinctKinds(docs: DocumentDTO[]): string[] {
  return kindCounts(docs).map((c) => c.value);
}

/** Sub-areas in use within a set (typically one life area's or one
 *  person's), deduped case-insensitively, most-used first, with counts —
 *  the drawer's per-area select and the drill-down "Sub-area" facet. */
export function subAreaCounts(docs: DocumentDTO[]): FacetCount[] {
  return countedValues(docs.map((d) => d.subArea));
}

export function distinctSubAreas(docs: DocumentDTO[]): string[] {
  return subAreaCounts(docs).map((c) => c.value);
}

/** Distinct tags across a set, deduped case-insensitively, most-used
 *  first. Drives the search "+ Filter" tag suggestions. */
export function distinctTags(docs: DocumentDTO[]): string[] {
  const counts = new Map<string, number>();
  for (const doc of docs) {
    for (const raw of doc.tags ?? []) {
      const t = raw.trim();
      if (!t) continue;
      const existing = [...counts.keys()].find((c) => c.toLowerCase() === t.toLowerCase()) ?? t;
      counts.set(existing, (counts.get(existing) ?? 0) + 1);
    }
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([t]) => t);
}

/** Years present in a set, newest first, with counts — the "Year" facet
 *  offered inside a kind drill-down. */
export function yearCounts(docs: DocumentDTO[]): FacetCount<number>[] {
  const counts = new Map<number, number>();
  for (const doc of docs) {
    const y = documentDate(doc).getFullYear();
    counts.set(y, (counts.get(y) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([value, count]) => ({ value, count }));
}

export function distinctYears(docs: DocumentDTO[]): number[] {
  return yearCounts(docs).map((c) => c.value);
}

/** The most recently added documents, for the index's "Recently added"
 *  shortcut. */
export function recentlyAdded(docs: DocumentDTO[], limit = 4): DocumentDTO[] {
  return [...docs]
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .slice(0, limit);
}

export function documentMatchesQuery(doc: DocumentDTO, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    doc.title,
    doc.issuer ?? '',
    doc.category ?? '',
    doc.subArea ?? '',
    doc.notes ?? '',
    ...(doc.tags ?? []),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

/* ── Vault (vital records) ────────────────────────────────────────────
   The Vault is a second surface over the same documents spine: a vault
   record is a document with documentCategory 'vital' ('retired' = a
   vault record kept but out of active use). Organised by kind and
   person — never by expiry — with an annual confirm-it's-current
   review. See PROJECT.md §Vault. */

export function isVaultRecord(d: DocumentDTO): boolean {
  return d.documentCategory === 'vital';
}

export function isRetiredRecord(d: DocumentDTO): boolean {
  return d.documentCategory === 'retired';
}

function vaultReviewAnchor(d: DocumentDTO): string {
  return d.vaultReviewedAt ?? d.createdAt;
}

/** When this record's next review falls due: last confirmation (or
 *  when it was added) plus its per-record cadence — 12 months unless
 *  the record asks for more or less (a lapsing passport at 6, mortgage
 *  papers at 24). Shared calendar math with the Worker cron via
 *  vaultNextReviewIso, so the app and the push can't disagree. */
export function vaultNextReview(d: DocumentDTO): Date | null {
  const next = new Date(vaultNextReviewIso(vaultReviewAnchor(d), d.vaultReviewMonths));
  return Number.isNaN(next.getTime()) ? null : next;
}

export function vaultReviewDue(d: DocumentDTO, now: Date): boolean {
  if (!isVaultRecord(d)) return false;
  const next = vaultNextReview(d);
  return next !== null && next.getTime() <= now.getTime();
}

/** Right-column label for a vault row: "review due" in the accent when
 *  the annual review has lapsed, else when it was last confirmed. */
export function vaultRowDate(d: DocumentDTO, now: Date): { label: string; warn: boolean } {
  if (vaultReviewDue(d, now)) return { label: 'review due', warn: true };
  const anchor = new Date(vaultReviewAnchor(d));
  if (Number.isNaN(anchor.getTime())) return { label: '', warn: false };
  return {
    label: anchor.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }),
    warn: false,
  };
}

/** Vault records grouped by kind (the `category` vocabulary, verbatim —
 *  it doubles as the Dropbox /Vault/ folder name). Groups sort
 *  alphabetically, Uncategorised last; rows by title. */
export function groupVaultByKind(docs: DocumentDTO[]): DocumentGroup[] {
  const groups = new Map<string, DocumentGroup>();
  for (const d of docs) {
    const label = d.category?.trim() || 'Uncategorised';
    const key = `vault:${label.toLowerCase()}`;
    let g = groups.get(key);
    if (!g) {
      g = { key, label, documents: [] };
      groups.set(key, g);
    }
    g.documents.push(d);
  }
  const sorted = [...groups.values()].sort((a, b) => {
    if (a.label === 'Uncategorised') return 1;
    if (b.label === 'Uncategorised') return -1;
    return a.label.localeCompare(b.label);
  });
  for (const g of sorted) g.documents.sort((a, b) => a.title.localeCompare(b.title));
  return sorted;
}

/**
 * Which icon a vault kind tile wears. Matched on the kind's label
 * (the free-text `category` facet), first hit wins — so "Australian
 * Passport", "Passports" and "Passport" all land on the same icon.
 * The shield is the fallback: an unrecognised kind still looks like
 * something the Vault protects. Rendered by VaultKindIcon.
 */
export type VaultIconName =
  | 'passport'
  | 'certificate'
  | 'rings'
  | 'flag'
  | 'scroll'
  | 'house'
  | 'idcard'
  | 'cross'
  | 'shield';

const VAULT_ICON_RULES: [RegExp, VaultIconName][] = [
  [/passport/i, 'passport'],
  [/marriage|wedding|partnership/i, 'rings'],
  [/citizen|naturali[sz]/i, 'flag'],
  [/will|testament|estate|power of attorney|directive/i, 'scroll'],
  [/property|deed|title|mortgage/i, 'house'],
  [/identity|id card|licen[cs]e|permit|visa/i, 'idcard'],
  [/medical|health|vaccin/i, 'cross'],
  // After the specific certificate-ish kinds above so "Marriage
  // Certificate" gets the rings, not the rosette.
  [/certificate|birth|diploma/i, 'certificate'],
];

export function vaultKindIconName(label: string): VaultIconName {
  for (const [re, name] of VAULT_ICON_RULES) {
    if (re.test(label)) return name;
  }
  return 'shield';
}

/** Status line for the Vault tile on the Today screen. */
export function vaultTileSummary(docs: DocumentDTO[] | undefined, now: Date): string | undefined {
  if (!docs) return undefined;
  const vault = docs.filter(isVaultRecord);
  if (vault.length === 0) return 'Start your vault';
  const due = vault.filter((d) => vaultReviewDue(d, now)).length;
  const noun = vault.length === 1 ? 'record' : 'records';
  if (due > 0) return `${vault.length} ${noun} · ${due} to review`;
  return `${vault.length} ${noun}`;
}

/**
 * The file name a document's stored file *should* carry — the shared
 * metadata-derived rule the server applies on upload and on every
 * metadata save. Settings' "tidy file names" control compares this
 * against the stored name to find files still wearing their original
 * camera-roll names (uploaded before the rule existed).
 */
export function expectedFileName(
  doc: DocumentDTO,
  peopleById: Map<string, string>,
): string | null {
  if (!doc.fileName) return null;
  const personNames = (doc.pertainsTo ?? [])
    .map((id) => peopleById.get(id))
    .filter((n): n is string => Boolean(n));
  return deriveDocumentFileName({
    title: doc.title,
    personNames,
    issuedDate: doc.issuedDate,
    createdAt: doc.createdAt,
    originalFileName: doc.fileName,
  });
}

/** Documents whose stored file name doesn't match the derived rule. */
export function fileNameMismatches(
  docs: DocumentDTO[],
  peopleById: Map<string, string>,
): DocumentDTO[] {
  return docs.filter((d) => {
    const expected = expectedFileName(d, peopleById);
    return expected !== null && expected !== d.fileName;
  });
}
